import { ID, STRING } from "@ehealth_tools/common/src/data/propertyTypes/property.types";
import { CompanyAffectationDTO } from "../data/users/company.dto";
import { TrustedPersonDTO } from "../data/users/trustedPerson.dto";
import { CheckUidResult } from "../data/users/user.definitions";
import { PatientDTO, PatientInfosDTO, PatientRegistration } from "../data/users/user.dto";
import { CoachRegistrationParams } from "../utils/coach.registration";
import { CompanyRegistration } from "../utils/company.registration";

/**
 * Interface for routes that register / create a new entity on the application
 */
export interface RegistrationRoutes<RP> {
    /** Check that uid is unique */
    isUserMailAvailable(params: { email: string | undefined }, routeParams?: RP): Promise<CheckUidResult>;
    /** Retrieve the conflicted patient data from user uid if it exists */
    retrieveConflictData(params: { email: string }, routeParams?: RP): Promise<PatientDTO | undefined>;
    /** Server side user registration */
    registerPatientWithCompany(params: PatientRegistrationWithCompany, routeParams?: RP): Promise<PatientRegistrationWithCompanyResult | null>;
    /** Register a company into Activmed database */
    registerCompany(params: CompanyRegistration, routeParams?: RP): Promise<REGISTER_COMPANY_STATUS>;
    /** Join a company (should be APA logged in) */
    joinCompany(params: JoinCompany, routeParams?: RP): Promise<boolean>;
    /** Register a coach in the application */
    registerCoach(params: CoachRegistrationParams, routeParams?: RP): Promise<COACH_REGISTRATION_RETURN_STATUS>;
    /** Register a patient server-side */
    registerPatientWithoutAffectation(params: PatientRegistration, routeParams?: RP): Promise<PATIENT_REGISTRATION_RETURN_STATUS>;
    /** Register a patient server-side from a mobile context */
    registerPatientFromMobile(params: PatientRegistration, routeParams?: RP): Promise<PATIENT_REGISTRATION_RETURN_STATUS>;
    /** Register an existing patient to a given company */
    affectPatientInCompany(params: PatientAffectationInCompany, routeParams?: RP): Promise<PatientRegistrationWithCompanyResult | null>;
    /** Update or create trusted  */
    updateOrCreateTrustedPerson(params: UpdateOrCreateTrustedPerson, routeParams?: RP): Promise<TrustedPersonDTO>;
    /** Invite to join */
    inviteToJoin(params: InviteToJoin, routeParams?: RP): Promise<void>;
}

export type TrustedPersonRegistration = Pick<TrustedPersonDTO, "givenName" | "familyName" | "bond" | "phoneNumber">
export type PatientRegistrationWithCompany = { patient: PatientRegistration & { referralId: number }, companyId: ID, idMSS?: STRING, trustedPerson: TrustedPersonRegistration };
export type PatientRegistrationWithCompanyResult = { patient: PatientDTO, infos: PatientInfosDTO, affectation?: CompanyAffectationDTO, trustedPerson?: TrustedPersonDTO };
export type PatientAffectationInCompany = { patientEmail: string, companyId: ID, idMSS?: string, trustedPerson: TrustedPersonRegistration }
export type UpdateOrCreateTrustedPerson = { patientId: ID, trustedPerson: Pick<TrustedPersonDTO, "bond" | "familyName" | "givenName" | "phoneNumber"> }
export type JoinCompany = { code: string };
export type InviteToJoin = { email: string, companyId: ID };

export enum REGISTER_COMPANY_STATUS {
    /** Registration and affectation of user to the company is effective */
    SUCCESS = "success",
    /** Unknown error occurred during process */
    UNKNOWN_ERROR = "unknown_error",
    /** Error with provided SIRET number of company */
    SIRET_ERROR = "siret_error",
    ALREADY_REGISTERED = "ALREADY_REGISTERED"
}

export enum COACH_REGISTRATION_RETURN_STATUS {
    /** Registration was a success */
    SUCCESS = "success",
    /** An unknown error occurred */
    UNKNOWN_ERROR = "unknown_error",
    /** Problem with provided mail : invalid or previously registered for another user */
    MAIL_ERROR = "mail_error"
}

export enum PATIENT_REGISTRATION_RETURN_STATUS {
    /** Registration was a success */
    SUCCESS = "success",
    /** An unknown error occurred */
    UNKNOWN_ERROR = "unknown_error",
    /** Problem with provided mail : invalid or previously registered for another user */
    MAIL_ERROR = "mail_error"
}